import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { fabricOptions, embroideryOptions } from '../utils/catalog';
import { useContent, resolveAsset } from '../contexts/ContentContext';
import SEO from '../components/SEO';

const mapCatalog = (c) => {
  const imgs = (c.images && c.images.length ? c.images : (c.image ? [c.image] : []))
    .filter(Boolean)
    .map(resolveAsset);
  return {
    id: c.id,
    name: c.name,
    description: c.description,
    origin: c.origin || c.craftsmen || '',
    availability: c.availability || 'Available',
    leadTime: c.lead_time || '',
    priceRange: c.price_range || '',
    hours: c.hours || '',
    craftsmen: c.craftsmen || '',
    image: imgs[0] || '',
    images: imgs,
  };
};

const CardCarousel = ({ images, alt, availability, availabilityClass, testId }) => {
  const [idx, setIdx] = React.useState(0);
  const total = images.length;
  if (total === 0) {
    return (
      <div className="fdp-card-image">
        {availability && <span className={`fdp-availability ${availabilityClass}`}>{availability}</span>}
      </div>
    );
  }
  const prev = (e) => { e.stopPropagation(); setIdx((i) => (i - 1 + total) % total); };
  const next = (e) => { e.stopPropagation(); setIdx((i) => (i + 1) % total); };

  return (
    <div className="fdp-card-image" data-testid={testId}>
      <img src={images[idx]} alt={`${alt} ${idx + 1}`} loading="lazy" />
      {availability && <span className={`fdp-availability ${availabilityClass}`}>{availability}</span>}
      {total > 1 && (
        <>
          <button type="button" className="fdp-carousel-nav fdp-carousel-prev" onClick={prev} aria-label="Previous image" data-testid={`${testId}-prev`}>
            <ChevronLeft size={16} />
          </button>
          <button type="button" className="fdp-carousel-nav fdp-carousel-next" onClick={next} aria-label="Next image" data-testid={`${testId}-next`}>
            <ChevronRight size={16} />
          </button>
          <div className="fdp-carousel-dots" data-testid={`${testId}-dots`}>
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`fdp-carousel-dot ${i === idx ? 'active' : ''}`}
                onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                aria-label={`Image ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const FabricDesignPage = () => {
  const [activeTab, setActiveTab] = React.useState('fabrics');
  const { catalog } = useContent();

  const dbFabrics = catalog.filter((c) => c.type === 'fabric').map(mapCatalog);
  const dbEmbroideries = catalog.filter((c) => c.type === 'embroidery').map(mapCatalog);
  const fabrics = dbFabrics.length ? dbFabrics : fabricOptions.map((f) => ({ ...f, images: f.image ? [f.image] : [] }));
  const embroideries = dbEmbroideries.length ? dbEmbroideries : embroideryOptions.map((e) => ({ ...e, images: e.image ? [e.image] : [] }));

  const getAvailabilityClass = (availability) => {
    if (availability === 'In Stock' || availability === 'Available') return 'available';
    if (availability === 'Limited Stock' || availability === 'Limited Slots') return 'limited';
    return 'order';
  };

  return (
    <div className="fabric-design-page">
      <SEO
        title="Craft Gallery — Heritage Fabrics & Embroidery"
        description="A curated guide to the heritage silks and artisanal embroidery techniques behind every Silk & Sentiment commission \u2014 from Banarasi tissue to Lucknowi chikankari."
        path="/design-studio"
      />
      {/* Header */}
      <header className="fdp-header">
        <div className="luxury-container">
          <Link to="/" className="fdp-back-link" data-testid="back-to-home">
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
          <h1 className="hero-large fdp-title">Craft Gallery</h1>
          <p className="body-large fdp-subtitle">
            A curated guide to our heritage fabrics and artisanal embroidery techniques
          </p>
          <p className="fdp-custom-note" data-testid="fdp-custom-note">
            We also curate and customize fabrics as per our clients&rsquo; unique preferences,
            ensuring every piece is truly one-of-a-kind.
          </p>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav className="fdp-tabs">
        <div className="luxury-container">
          <div className="fdp-tab-buttons">
            <button
              className={`fdp-tab-btn ${activeTab === 'fabrics' ? 'active' : ''}`}
              onClick={() => setActiveTab('fabrics')}
              data-testid="tab-fabrics"
            >
              Fabrics
            </button>
            <button
              className={`fdp-tab-btn ${activeTab === 'embroidery' ? 'active' : ''}`}
              onClick={() => setActiveTab('embroidery')}
              data-testid="tab-embroidery"
            >
              Embroidery
            </button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="fdp-content section-padding">
        <div className="luxury-container">
          {activeTab === 'fabrics' ? (
            <>
              <div className="fdp-section-header">
                <h2 className="heading-1">Heritage Fabrics</h2>
                <p className="body-regular">Each fabric tells a story of tradition and excellence</p>
              </div>
              <div className="fdp-grid fdp-grid--info">
                {fabrics.map((fabric) => (
                  <article
                    key={fabric.id}
                    className="fdp-card fdp-card--info"
                    data-testid={`fabric-card-${fabric.id}`}
                  >
                    <CardCarousel
                      images={fabric.images}
                      alt={fabric.name}
                      availability={fabric.availability}
                      availabilityClass={getAvailabilityClass(fabric.availability)}
                      testId={`fabric-carousel-${fabric.id}`}
                    />
                    <div className="fdp-card-content">
                      <h3 className="heading-3">{fabric.name}</h3>
                      <p className="body-small fdp-origin">{fabric.origin}</p>
                      <p className="body-small fdp-description">{fabric.description}</p>
                      <div className="fdp-meta">
                        <span className="fdp-meta-item">
                          <strong>Lead Time:</strong> {fabric.leadTime}
                        </span>
                        <span className="fdp-meta-item">
                          <strong>Range:</strong> {fabric.priceRange}
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="fdp-section-header">
                <h2 className="heading-1">Artisanal Embroidery</h2>
                <p className="body-regular">Centuries-old techniques brought to life by master craftsmen</p>
              </div>
              <div className="fdp-grid fdp-grid--info">
                {embroideries.map((embroidery) => (
                  <article
                    key={embroidery.id}
                    className="fdp-card fdp-card--info"
                    data-testid={`embroidery-card-${embroidery.id}`}
                  >
                    <CardCarousel
                      images={embroidery.images}
                      alt={embroidery.name}
                      availability={embroidery.availability}
                      availabilityClass={getAvailabilityClass(embroidery.availability)}
                      testId={`embroidery-carousel-${embroidery.id}`}
                    />
                    <div className="fdp-card-content">
                      <h3 className="heading-3">{embroidery.name}</h3>
                      <p className="body-small fdp-origin">{embroidery.craftsmen}</p>
                      <p className="body-small fdp-description">{embroidery.description}</p>
                      <div className="fdp-meta">
                        <span className="fdp-meta-item">
                          <strong>Crafting Time:</strong> {embroidery.hours}
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}

          {/* Closing CTA */}
          <div className="fdp-closing">
            <p className="fdp-closing-text">
              Found a fabric or technique that resonates? Begin a conversation with our atelier.
            </p>
            <div className="fdp-closing-actions">
              <Link to="/contact" className="btn-primary" data-testid="fdp-contact-cta">
                Begin a Conversation
              </Link>
              <Link to="/#bespoke" className="btn-secondary" data-testid="fdp-bespoke-cta">
                Explore the Bespoke Journey
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FabricDesignPage;
